import React, { useState, useCallback, memo, useEffect, useRef, useMemo } from 'react'
import { View, Animated, ScrollView, ViewPropTypes } from 'react-native'
import { useSelector } from 'react-redux'
import BottomNavigation, { FullTab } from 'react-native-material-bottom-navigation'
import PropTypes from 'prop-types'

import Txt from '../Txt'
import LoadInformation from './LoadInformation'
import BidForm from './BidForm'
import BookForm from './BookForm'
import useStyles from './useStyles'
import Icon from '../Icons'
import { useTheme } from '../../theming/ThemeProvider'
import { getLoadStatusData } from '../../tools/dataParse'
import { usePlatforms } from '../../hooks/usePlatforms'
import { loadDetailSelector } from '../../redux/loadDetails/loadDetailsSelector'

const LoadDetails = (props) => {
  const { load, module, showMap, containerStyle, disableScroll } = props

  const [showBidForm, setShowBidForm] = useState(false)
  const [showBookForm, setShowBookForm] = useState(false)
  const { isTablet } = usePlatforms()
  const { theme } = useTheme()
  const styles = useStyles(theme, isTablet)
  const contentScrollRef = useRef()

  const [defaultTabs] = useState([
    {
      key: 'load-description',
      icon: 'Loads',
      barColor: theme.WHITE,
      label: 'Order Details'
    },
    {
      key: 'tractor-description',
      icon: 'Tractors',
      barColor: theme.WHITE,
      label: 'Tractor Details'
    },
    {
      key: 'bid-history',
      icon: 'Offers',
      barColor: theme.WHITE,
      label: 'Bid History'
    }
  ])

  const [tabs, setTabs] = useState(defaultTabs)
  const [activeTab, setActiveTab] = useState('load-description')
  const [loadStatus, setLoadStatus] = useState()
  const { tractorDetails } = useSelector(loadDetailSelector)

  const showTabs = useMemo(() => {
    return module === 'my-offers' || module === 'offers-history' ||
      module === 'assigned-orders' || module === 'ready-to-invoice'
  }, [module])

  useEffect(() => {
    setTabs(module === 'my-offers' || module === 'offers-history'
      ? defaultTabs.filter(tab => tab.key !== 'tractor-description')
      : defaultTabs.filter(tab => tab.key !== 'bid-history')
    )
    setActiveTab('load-description')
  }, [module, defaultTabs])

  const animatedValue = useRef(new Animated.Value(0)).current

  const frontInterpolate = animatedValue.interpolate({
    inputRange: [0, 180],
    outputRange: ['0deg', '180deg']
  })

  const backInterpolate = animatedValue.interpolate({
    inputRange: [0, 180],
    outputRange: ['180deg', '360deg']
  })

  const frontOpacity = animatedValue.interpolate({
    inputRange: [70, 120],
    outputRange: [1, 0]
  })

  const backOpacity = animatedValue.interpolate({
    inputRange: [70, 120],
    outputRange: [0, 1]
  })

  const frontZIndex = animatedValue.interpolate({
    inputRange: [0, 180],
    outputRange: [1, 0]
  })

  const backZIndex = animatedValue.interpolate({
    inputRange: [0, 180],
    outputRange: [0, 1]
  })

  const flipCard = useCallback(() => {
    if (!showBidForm && !showBookForm) {
      Animated.spring(animatedValue, {
        toValue: 0,
        friction: 8,
        tension: 10
      }).start()
    } else {
      Animated.spring(animatedValue, {
        toValue: 180,
        friction: 8,
        tension: 10
      }).start()
    }
  }, [animatedValue, showBidForm, showBookForm])

  const frontAnimatedStyle = {
    transform: [{ rotateY: frontInterpolate }]
  }
  const backAnimatedStyle = {
    transform: [{ rotateY: backInterpolate }]
  }

  const handleBidForm = () => {
    setShowBidForm(!showBidForm)
  }

  const handleBookForm = () => {
    setShowBookForm(!showBookForm)
  }

  useEffect(() => {
    flipCard()
  }, [flipCard, showBookForm])

  useEffect(() => {
    flipCard()
  }, [flipCard, showBidForm])

  useEffect(() => {
    setLoadStatus(getLoadStatusData(module, load))
    setShowBidForm(false)
    setShowBookForm(false)
    setActiveTab('load-description')
    const contentScroll = contentScrollRef.current
    if (contentScroll) {
      contentScroll.scrollTo({
        y: 0,
        animated: true
      })
    }
  }, [load, module])

  const Tabs = useMemo(() => (
    <View style={{ flexGrow: 0 }}>
      <BottomNavigation
        activeTab={activeTab}
        onTabPress={newTab => setActiveTab(newTab.key)}
        renderTab={({ tab, isActive }) => (
          <FullTab
            isActive={isActive}
            key={tab.key}
            label={isTablet ? tab.label : ''}
            labelProps={{
              style: {
                color: theme[isActive ? 'AZTEC' : 'GEYSER'],
                fontWeight: 'bold',
                fontSize: 12
              }
            }}
            renderIcon={({ isActive: iconActive }) => (
              <Icon
                name={tab.icon}
                height={20}
                width={20}
                fill={theme[iconActive ? 'AZTEC' : 'GEYSER']}
              />
            )}
          />
        )}
        tabs={tabs}
        style={styles.tabs}
      />
    </View>
  ), [activeTab, isTablet, styles.tabs, tabs, theme])

  return (
    <Animated.View style={containerStyle}>
      <Animated.View
        style={[
          styles.flipCard,
          frontAnimatedStyle,
          {
            opacity: frontOpacity,
            zIndex: frontZIndex,
            display: 'flex'
          }
        ]}
      >
        <View
          style={{
            paddingHorizontal: isTablet ? 30 : 20,
            marginBottom: 10,
            flexGrow: 0
          }}
        >
          <Txt
            text={`Order No. ${load?.orderNumber || '--'}`}
            identity='h3'
            selectable
          />
          <View style={styles.description}>
            <Txt
              text={`Movement No. ${load?.movementNumber || '--'}`}
              identity='body2'
              color='SLATE_GRAY'
              selectable
            />
            {Boolean(loadStatus) && (
              <View
                style={[
                  styles.status,
                  { backgroundColor: theme[loadStatus.color] }
                ]}
              >
                <Txt
                  text={loadStatus.name}
                  identity='body3'
                  color='WHITE'
                />
              </View>
            )}
          </View>
        </View>
        {showTabs && isTablet && Tabs}
        <ScrollView
          ref={contentScrollRef}
          scrollEnabled={!disableScroll}
          style={{ flex: 1 }}
        >
          <LoadInformation
            module={module}
            activeTab={activeTab}
            load={load}
            tractorDetails={tractorDetails}
            onBid={handleBidForm}
            onBook={handleBookForm}
            showMap={showMap}
          />
        </ScrollView>
        {showTabs && !isTablet && Tabs}
      </Animated.View>
      <Animated.View
        style={[
          styles.flipCardBack,
          backAnimatedStyle,
          {
            opacity: backOpacity,
            zIndex: backZIndex
          }
        ]}
      >
        {showBookForm
          ? (
            <BookForm
              rate={load?.rate}
              orderNumber={load?.orderNumber}
              movementNumber={load?.movementNumber}
              offerHistory={load?.offer_history}
              onClose={handleBookForm}
              module={module}
            />
          ) : (
            <BidForm
              rate={load?.rate}
              orderNumber={load?.orderNumber}
              movementNumber={load?.movementNumber}
              offerHistory={load?.offer_history}
              onClose={handleBidForm}
              module={module}
            />
          )}
      </Animated.View>
    </Animated.View>
  )
}

LoadDetails.propTypes = {
  load: PropTypes.object,
  module: PropTypes.string,
  showMap: PropTypes.bool,
  disableScroll: PropTypes.bool,
  containerStyle: ViewPropTypes.style
}

LoadDetails.defaultProps = {
  load: {},
  module: '',
  showMap: false,
  disableScroll: false
}

const arePropsEqual = (prevProps, nextProps) => {
  return JSON.stringify(prevProps.load) === JSON.stringify(nextProps.load) &&
    JSON.stringify(prevProps.containerStyle) === JSON.stringify(nextProps.containerStyle) &&
    prevProps.module === nextProps.module &&
    prevProps.showMap === nextProps.showMap &&
    prevProps.disableScroll === nextProps.disableScroll
}

export default memo(LoadDetails, arePropsEqual)
